import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { DocumentHeader } from "../../components/DocumentHeader";
import { SignaturePad } from "../../components/SignaturePad";
import { DocumentClosureBar } from "../../components/DocumentClosureBar";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { api, formatApiError, UPLOADS_BASE } from "../../lib/api";
import { useAuth, hasPerm } from "../../context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Printer, Save, Mail, PackagePlus, Search, Package } from "lucide-react";

const DEFAULT_MENTIONS = "Devis valable 30 jours à compter de sa date d'émission. Ce devis ne constitue pas une facture. Tout travail supplémentaire non prévu fera l'objet d'un devis complémentaire.";

export default function DevisFormPage() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const sigRef = useRef(null);
  const [shop, setShop] = useState(null);
  const [mailOpen, setMailOpen] = useState(false);
  const [mailTo, setMailTo] = useState("");
  const [mailSubject, setMailSubject] = useState("");
  const [mailMessage, setMailMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [articles, setArticles] = useState([]);
  const [articlePickerOpen, setArticlePickerOpen] = useState(false);
  const [articleSearch, setArticleSearch] = useState("");
  const [data, setData] = useState({
    numero: "", client_id: searchParams.get("client_id") || null,
    client_nom: searchParams.get("client_nom") || "", client_tel: searchParams.get("client_tel") || "",
    client_email: searchParams.get("client_email") || "",
    items: [], mentions_legales: DEFAULT_MENTIONS,
  });

  useEffect(() => {
    (async () => {
      const [shopsRes] = await Promise.all([api.get("/shops")]);
      const myShop = shopsRes.data.find((s) => s.id === user.effective_shop_id);
      if (!myShop && isNew) {
        toast.error("Aucune boutique n'est assignée à votre compte. Contactez un administrateur.");
      }
      setShop(myShop || shopsRes.data[0]);
      if (hasPerm(user, "stock", "view")) {
        api.get("/stock").then((r) => setArticles(r.data)).catch(() => {});
      }
      if (!isNew) {
        const { data: item } = await api.get(`/devis/${id}`);
        setData(item);
      }
    })();
  }, [id]);

  const today = new Date().toLocaleDateString("fr-FR");
  const total = data.items.reduce((s, i) => s + i.prix_unitaire * i.quantite, 0);

  const addItem = () => setData({ ...data, items: [...data.items, { nom: "", prix_unitaire: 0, quantite: 1 }] });
  const updateItem = (idx, field, val) => {
    const items = [...data.items];
    items[idx] = { ...items[idx], [field]: val };
    setData({ ...data, items });
  };
  const removeItem = (idx) => setData({ ...data, items: data.items.filter((_, i) => i !== idx) });

  const addArticleFromStock = (article) => {
    setData((d) => ({
      ...d,
      items: [...d.items, { nom: article.nom, prix_unitaire: Number(article.prix_ttc ?? article.prix ?? 0), quantite: 1 }],
    }));
    toast.success(`« ${article.nom} » ajouté au devis`);
  };

  const articleSearchQ = articleSearch.trim().toLowerCase();
  const filteredArticles = articles.filter((a) => {
    if (!articleSearchQ) return true;
    return (a.nom || "").toLowerCase().includes(articleSearchQ)
      || (a.categorie || "").toLowerCase().includes(articleSearchQ)
      || (a.code || "").toLowerCase().includes(articleSearchQ);
  });
  const articlesEnStock = filteredArticles.filter((a) => Number(a.quantite ?? 0) > 0);
  const articlesHorsStock = filteredArticles.filter((a) => Number(a.quantite ?? 0) <= 0);

  const hasEditPerm = data.is_shared_to_me
    ? data.share_mode === "write"
    : (isNew ? hasPerm(user, "devis", "create") : hasPerm(user, "devis", "edit"));
  const isCloturee = data.status === "cloture";
  const canEdit = hasEditPerm && !isCloturee;

  const save = async () => {
    const signature_data = sigRef.current?.toDataURL() || data.signature_data || "";
    if (isNew && !shop) {
      toast.error("Impossible de créer : aucune boutique assignée à votre compte.");
      return;
    }
    try {
      if (isNew) {
        const { data: created } = await api.post("/devis", { ...data, shop_id: shop.id, signature_data });
        toast.success(`Devis ${created.numero} créé`);
        navigate(`/devis/${created.id}`);
      } else {
        await api.patch(`/devis/${id}`, { ...data, signature_data });
        toast.success("Devis mis à jour");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openMailDialog = () => {
    setMailTo(data.client_email || "");
    setMailSubject(`Votre devis ${data.numero || ""} - ${shop?.nom || ""}`);
    setMailMessage(`Bonjour ${data.client_nom || ""},\n\nVeuillez trouver ci-joint votre devis ${data.numero || ""}.\n\nCordialement,\n${shop?.nom || ""}`);
    setMailOpen(true);
  };

  const sendMail = async () => {
    if (!mailTo) { toast.error("Saisissez l'adresse e-mail du client"); return; }
    setSending(true);
    try {
      const res = await api.post(`/devis/${id}/send-email`, { to_email: mailTo, subject: mailSubject, message: mailMessage });
      toast.success(`Devis envoyé à ${res.data.sent_to}`);
      setMailOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout title="Devis">
      <div className="a4-sheet max-w-4xl mx-auto p-4 sm:p-8 md:p-12 rounded-md">
        <DocumentHeader shop={shop} numero={data.numero || "(auto)"} />
        {data.is_shared_to_me && (
          <p className="text-xs text-center text-muted-foreground mb-4" data-testid="devis-shared-banner">
            Document partagé par {data.shared_by_label} — {data.share_mode === "write" ? "lecture / écriture" : "lecture seule"}
          </p>
        )}
        <h2 className="font-heading text-xl font-bold text-center mb-6">DEVIS</h2>

        <DocumentClosureBar
          module="devis"
          id={isNew ? null : id}
          data={data}
          canEdit={hasEditPerm}
          canDelete={hasPerm(user, "devis", "delete")}
          onUpdated={setData}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 text-sm">
          <div><span className="font-semibold">Vendeur:</span> {data.vendeur_nom || `${user.prenom} ${user.nom}`}</div>
          <div><span className="font-semibold">Date:</span> {data.date ? `${data.date}${data.heure ? " " + data.heure : ""}` : today}</div>
          <Input data-testid="devis-client-nom" disabled={!canEdit} placeholder="Nom client" value={data.client_nom} onChange={(e) => setData({ ...data, client_nom: e.target.value })} />
          <Input data-testid="devis-client-tel" disabled={!canEdit} placeholder="Téléphone" value={data.client_tel} onChange={(e) => setData({ ...data, client_tel: e.target.value })} />
        </div>

        <div className="mb-6">
          <div className="flex justify-between items-center mb-2 gap-2 flex-wrap">
            <p className="font-semibold text-sm">Lignes du devis</p>
            {canEdit && (
              <div className="flex gap-2">
                {hasPerm(user, "stock", "view") && (
                  <Button size="sm" variant="outline" data-testid="devis-add-article" onClick={() => { setArticleSearch(""); setArticlePickerOpen(true); }} className="gap-1">
                    <PackagePlus size={14} />Ajouter article
                  </Button>
                )}
                <Button size="sm" variant="outline" data-testid="devis-add-item" onClick={addItem} className="gap-1"><Plus size={14} />Ligne</Button>
              </div>
            )}
          </div>
          {data.items.map((item, idx) => (
            <div key={idx} className="grid grid-cols-2 sm:grid-cols-12 gap-2 mb-2 items-center">
              <Input disabled={!canEdit} data-testid={`devis-item-nom-${idx}`} className="col-span-2 sm:col-span-6" placeholder="Désignation" value={item.nom} onChange={(e) => updateItem(idx, "nom", e.target.value)} />
              <Input disabled={!canEdit} data-testid={`devis-item-qte-${idx}`} type="number" className="col-span-1 sm:col-span-2" value={item.quantite} onChange={(e) => updateItem(idx, "quantite", Number(e.target.value))} />
              <Input disabled={!canEdit} data-testid={`devis-item-prix-${idx}`} type="number" className="col-span-1 sm:col-span-3" value={item.prix_unitaire} onChange={(e) => updateItem(idx, "prix_unitaire", Number(e.target.value))} />
              {canEdit && <button data-testid={`devis-item-remove-${idx}`} onClick={() => removeItem(idx)} className="text-destructive justify-self-end sm:justify-self-auto"><Trash2 size={16} /></button>}
            </div>
          ))}
          <p className="text-right font-bold mt-2">Total: {total.toFixed(2)} €</p>
        </div>

        <label className="text-sm font-semibold">Mentions légales</label>
        <Textarea data-testid="devis-mentions" disabled={!canEdit} className="mb-6 h-20" value={data.mentions_legales} onChange={(e) => setData({ ...data, mentions_legales: e.target.value })} />

        <label className="text-sm font-semibold block mb-2">Signature (bon pour accord)</label>
        <SignaturePad ref={sigRef} testId="devis-signature-pad" disabled={!canEdit} />
        {isCloturee && (
          <p className="text-xs text-muted-foreground mt-2">
            Document clôturé : la signature déjà apposée n'est plus modifiable.
          </p>
        )}

        <div className="flex gap-3 mt-6 no-print">
          {canEdit && <Button data-testid="devis-save-button" onClick={save} className="gap-2"><Save size={16} />Enregistrer</Button>}
          {!isNew && (
            <a href={`${api.defaults.baseURL}/devis/${id}/pdf`} target="_blank" rel="noreferrer">
              <Button variant="outline" data-testid="devis-pdf-button" className="gap-2"><Printer size={16} />PDF</Button>
            </a>
          )}
          {!isNew && (
            <Button variant="outline" className="gap-2" onClick={openMailDialog} data-testid="devis-send-email-button">
              <Mail size={16} />Envoyer par e-mail
            </Button>
          )}
        </div>
      </div>

      <Dialog open={mailOpen} onOpenChange={setMailOpen}>
        <DialogContent data-testid="devis-mail-dialog">
          <DialogHeader><DialogTitle>Envoyer le devis par e-mail</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Adresse e-mail du client" value={mailTo} onChange={(e) => setMailTo(e.target.value)} data-testid="devis-mail-to" />
            <Input placeholder="Objet" value={mailSubject} onChange={(e) => setMailSubject(e.target.value)} data-testid="devis-mail-subject" />
            <Textarea className="h-32" placeholder="Message" value={mailMessage} onChange={(e) => setMailMessage(e.target.value)} data-testid="devis-mail-message" />
            <p className="text-xs text-muted-foreground">Le PDF du devis sera joint automatiquement. L'e-mail sera envoyé depuis l'adresse configurée pour votre boutique (Admin &gt; Boutiques &amp; Dépôts).</p>
          </div>
          <DialogFooter>
            <Button onClick={sendMail} disabled={sending} className="gap-2" data-testid="devis-mail-send-button">
              <Mail size={16} />{sending ? "Envoi..." : "Envoyer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={articlePickerOpen} onOpenChange={setArticlePickerOpen}>
        <DialogContent data-testid="devis-article-picker-dialog" className="max-h-[85vh] flex flex-col">
          <DialogHeader><DialogTitle>Ajouter un article du stock</DialogTitle></DialogHeader>
          <div className="relative shrink-0">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              className="pl-9"
              placeholder="Rechercher un article, une catégorie, un ID..."
              value={articleSearch}
              onChange={(e) => setArticleSearch(e.target.value)}
              data-testid="devis-article-search"
            />
          </div>
          <div className="overflow-y-auto space-y-4 pr-1 -mr-1">
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">En stock ({articlesEnStock.length})</p>
              {articlesEnStock.length === 0 ? (
                <p className="text-xs text-muted-foreground italic py-2">Aucun article en stock ne correspond.</p>
              ) : (
                <div className="space-y-1" data-testid="devis-article-list-en-stock">
                  {articlesEnStock.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      data-testid={`devis-article-pick-${a.id}`}
                      onClick={() => addArticleFromStock(a)}
                      className="w-full flex items-center gap-3 rounded-lg border border-border bg-card p-2 hover:border-primary transition-colors duration-200 text-left"
                    >
                      <div className="w-9 h-9 rounded-md bg-secondary overflow-hidden flex items-center justify-center shrink-0">
                        {a.photo_url ? <img src={`${UPLOADS_BASE}${a.photo_url}`} className="w-full h-full object-cover" alt="" /> : <Package size={15} className="text-muted-foreground" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{a.nom}</p>
                        <p className="text-xs text-muted-foreground">{a.categorie || "—"} · Qté: {a.quantite}</p>
                      </div>
                      <p className="text-sm font-semibold shrink-0">{Number(a.prix_ttc ?? a.prix ?? 0).toFixed(2)} €</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Hors stock ({articlesHorsStock.length})</p>
              {articlesHorsStock.length === 0 ? (
                <p className="text-xs text-muted-foreground italic py-2">Aucun article hors stock ne correspond.</p>
              ) : (
                <div className="space-y-1" data-testid="devis-article-list-hors-stock">
                  {articlesHorsStock.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      data-testid={`devis-article-pick-${a.id}`}
                      onClick={() => addArticleFromStock(a)}
                      className="w-full flex items-center gap-3 rounded-lg border border-slate-400/40 bg-slate-500/5 p-2 hover:border-primary transition-colors duration-200 text-left opacity-80"
                    >
                      <div className="w-9 h-9 rounded-md bg-secondary overflow-hidden flex items-center justify-center shrink-0">
                        {a.photo_url ? <img src={`${UPLOADS_BASE}${a.photo_url}`} className="w-full h-full object-cover" alt="" /> : <Package size={15} className="text-muted-foreground" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{a.nom}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{a.categorie || "—"} · Hors stock</p>
                      </div>
                      <p className="text-sm font-semibold shrink-0">{Number(a.prix_ttc ?? a.prix ?? 0).toFixed(2)} €</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => setArticlePickerOpen(false)} data-testid="devis-article-picker-close">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
